import { useCallback, useEffect, useState } from "react";
import { BIDDING_TYPES } from "../../utils";
import { MaterialType } from "../../types";
import { isGeneralDataStepComplete } from "./generalDataStep";
import { areServicesComplete } from "./serviceDetail";

type StepGatingArgs = {
  activeStep: number;
  ordersToSubmit: any[];
  selectedType: any;
  selectedComplexType: any;
  generalBidData: any;
};

/**
 * The per-step Next-button gating, lifted verbatim from Order.tsx. Owns
 * `disabled` and returns `setDisabled` too so the container's step transitions
 * can still force it true on Next (matching the original). Behaviour-preserving:
 * same predicates, same dependency list.
 */
export const useStepGating = ({
  activeStep,
  ordersToSubmit,
  selectedType,
  selectedComplexType,
  generalBidData,
}: StepGatingArgs) => {
  const [disabled, setDisabled] = useState(true);

  const verifyThirdStep = useCallback(() => {
    const initialPrice = ordersToSubmit?.every((el: any) => el.initialPrice > 0);
    const quantityForGoods = ordersToSubmit
      ?.filter((el: any) => el.type !== MaterialType.SERVICES)
      ?.every((el: any) => el.quantity > 0);
    const name = ordersToSubmit?.every((el: any) => el.name);
    const startSupplyDate = ordersToSubmit?.every(
      (el: any) => el.startSupplyDate
    );
    const endSupplyDate = ordersToSubmit?.every((el: any) => el.endSupplyDate);
    const purpose = ordersToSubmit
      ?.filter((el: any) => el.type === MaterialType.SERVICES)
      ?.every((el: any) => el.purpose);
    const isService =
      ordersToSubmit?.filter((el: any) => el.type === MaterialType.SERVICES)
        ?.length > 0;
    const minimumDecrement = ordersToSubmit?.every(
      (el: any) => el.minimumDecrement > 0
    );
    if (selectedType?.value === BIDDING_TYPES.NORMAL) {
      if (isService) {
        name && purpose && quantityForGoods && startSupplyDate && endSupplyDate
          ? setDisabled(false)
          : setDisabled(true);
      } else {
        quantityForGoods && name && startSupplyDate && endSupplyDate
          ? setDisabled(false)
          : setDisabled(true);
      }
    }
    if (selectedType?.value === BIDDING_TYPES.INVERSE) {
      if (isService) {
        name && initialPrice && minimumDecrement && purpose && quantityForGoods
          ? setDisabled(false)
          : setDisabled(true);
      } else {
        name && quantityForGoods && initialPrice && minimumDecrement
          ? setDisabled(false)
          : setDisabled(true);
      }
    }
    if (selectedComplexType?.value === BIDDING_TYPES.JOINT) {
      if (selectedType?.value === BIDDING_TYPES.INVERSE) {
        quantityForGoods && initialPrice && minimumDecrement
          ? setDisabled(false)
          : setDisabled(true);
      } else {
        quantityForGoods ? setDisabled(false) : setDisabled(true);
      }
    }
    // SCRUM-114: on top of the rules above, every service needs its detail complete.
    if (!areServicesComplete(ordersToSubmit)) setDisabled(true);
  }, [ordersToSubmit, selectedType, selectedComplexType]);

  useEffect(() => {
    if (activeStep === 1)
      selectedType !== null && selectedComplexType !== null
        ? setDisabled(false)
        : setDisabled(true);
    if (activeStep === 2) {
      if (selectedComplexType?.value === BIDDING_TYPES.JOINT) {
        ordersToSubmit?.length > 1 ? setDisabled(false) : setDisabled(true);
      } else {
        ordersToSubmit?.length > 0 && ordersToSubmit?.length === 1
          ? setDisabled(false)
          : setDisabled(true);
      }
    }
    if (activeStep === 3) {
      ordersToSubmit?.every((el: any) => el.suppliers?.length > 0)
        ? setDisabled(false)
        : setDisabled(true);
    }
    if (activeStep === 4) verifyThirdStep();
    if (activeStep === 5)
      setDisabled(
        !isGeneralDataStepComplete({ generalBidData, ordersToSubmit })
      );
    if (activeStep === 6) setDisabled(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ordersToSubmit, activeStep, selectedType, selectedComplexType, generalBidData]);

  return { disabled, setDisabled };
};
